/** @jsx jsx */
import { type ReactNode, memo, useRef } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useResizeObserver } from '@atlassian/jira-react-use-resize-observer/src/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';

const PopupContentComponent = ({
	children,
	update,
	isInteractive,
}: {
	children: ReactNode;
	update: () => void;
	isInteractive?: boolean;
}) => {
	const componentRef = useRef(null);

	useResizeObserver({
		ref: componentRef,
		onResize: update,
	});

	return (
		<div
			css={popoverStyles}
			ref={componentRef}
			data-testid="hover-popover.ui.popup-content.hover-popover-content"
			aria-hidden={!isInteractive}
			// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
			tabIndex={isInteractive ? 0 : undefined}
		>
			{children}
			<ShortcutScope />
		</div>
	);
};

export const PopupContent = memo(PopupContentComponent);

const popoverStyles = css({
	backgroundColor: token('elevation.surface.overlay'),
	paddingInline: token('space.150'),
	paddingBlock: token('space.075'),
	borderRadius: token('border.radius'),
	boxShadow: token('elevation.shadow.overlay'),
	fontBody: token('font.body'),
	fontWeight: token('font.weight.regular'),
	overflowWrap: 'break-word',
	maxHeight: '300px',
	wordBreak: 'break-word',
	overflow: 'auto',
	'&:focus': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});
